import { useTranslation } from "react-i18next";
import { clockOf } from "@/app/lib/duration";
import type { DebriefResult } from "./debrief";
import { civilDate } from "./logs.converter";
import type { SessionLogSummaryModel } from "./logs.model";

/**
 * THE SENTENCES A HISTORY ROW PRINTS.
 *
 * A HOOK AND NOT A PURE MODULE, for the reason `sessions/use-line-text.ts` gives at length:
 * every one of these is a SENTENCE, worded once per language. A helper that concatenated
 * `format + " · " + rounds` would have taken the separator, the word order and the unit away
 * from the translator, permanently — and `boiler/no-concatenated-translation` exists because
 * somebody always tries.
 *
 * SO EVERY SHAPE HAS ITS OWN KEY rather than one key with optional halves. A log can carry a
 * round count, a clock, both or neither, and "AMRAP ·  · " is what one key with holes in it
 * produces.
 *
 * ⚠️ `head` IS GONE, AND ITS ABSENCE IS THE POINT. It joined the day and the title into one
 * 13px line — "Wed, 25 Aug · Deadlift + Swing AMRAP" — which is what made a history row read
 * as a caption rather than as a session. The row is card-shaped now: the DATE sits where a
 * deck card puts its estimate and the TITLE is a heading beneath it, so there is nothing left
 * to join.
 *
 * ⚠️ THE SIX FORMAT NAMES ARE ALSO IN THE `sessions` NAMESPACE. That is a copy, and it is
 * deliberate: `useLineText` is not exported from the sessions barrel (a feature is imported
 * through its `index.ts` and that barrel publishes `useCardText`, not this), and reaching for
 * another feature's translation namespace by string is a coupling nothing would catch when it
 * broke. Six words, duplicated once, against an import the architecture forbids.
 */
export interface LogText {
  /** "Wed, 25 Aug" — built by `Intl`, in the athlete's own language. */
  day: (log: SessionLogSummaryModel) => string;
  /** "RX" / "Scaled". */
  load: (log: SessionLogSummaryModel) => string;
  /** "AMRAP · 9 rounds in 12:00" — line two, and what the session actually scored. */
  result: (log: SessionLogSummaryModel) => string;
  /** "Perf 4 · Fatigue 4 · Feeling 3" — line three, or null where no debrief was written. */
  debrief: (log: SessionLogSummaryModel) => string | null;
  /** What the heart's press does next, for a screen reader. */
  heart: (log: SessionLogSummaryModel) => string;
}

export function useLogText(): LogText {
  const { t, i18n } = useTranslation("logs");

  /**
   * The SHAPE of a date is a property of a locale, not a string anybody translates, so every
   * language this app adds gets it right without a translator being asked.
   */
  const day = (log: SessionLogSummaryModel) =>
    new Intl.DateTimeFormat(i18n.language, {
      weekday: "short",
      month: "short",
      day: "numeric",
    }).format(civilDate(log.performedOn));

  const result = (log: SessionLogSummaryModel) => {
    /**
     * A ROW THE DECK OPENED AND NOBODY CLOSED has no score, because no session finished. It
     * says so rather than printing a format on its own, which would read as a workout that
     * was done and scored nothing.
     */
    if (log.outcome !== "completed") return t(`history.result.${log.outcome}`);

    const format = t(`history.format.${log.format}`, { defaultValue: log.format });
    const clock = log.elapsedSeconds === null ? null : clockOf(log.elapsedSeconds);
    /**
     * 🚨 THE ROUND COUNT IS BOTH HALVES OF THE SCORE, THROUGH THE SAME WORDS THE RECAP USES.
     *
     * This row printed `log.rounds` bare, so a session scored 5 + 12 read as "5 rounds" in the
     * list while the detail screen — corrected by BUD-372 — read "5 + 12" for the same row. Two
     * private spellings of one figure are how that happened, which is exactly what `resultText`
     * exists to prevent; the row now goes through it (BUD-373). A summary row carries no scoring
     * rule (the format's `scoredBy` is on the plan, never on the log), so it still words whichever
     * of the two raw facts it holds — what changed is that the partial round is no longer dropped.
     */
    const rounds =
      log.rounds === null
        ? null
        : resultText(t, { kind: "rounds", value: log.rounds, plus: log.partialReps });

    const scored =
      rounds !== null && clock !== null
        ? t("history.result.roundsTime", { format, rounds, clock })
        : rounds !== null
          ? t("history.result.rounds", { format, rounds })
          : clock !== null
            ? t("history.result.time", { format, clock })
            : t("history.result.plain", { format });

    /**
     * THE CAP IS PART OF THE SCORE. "9 rounds" and "9 rounds, stopped by the clock" are two
     * different sessions, and the engine's whole censoring model exists because the second
     * one gets filed as the first.
     */
    return log.capHit === true ? t("history.result.capped", { result: scored }) : scored;
  };

  return {
    day,
    load: (log) => t(`history.load.${log.load}`),
    result,
    /**
     * ALL THREE OR NONE. The debrief writes the three together; a row with one of them is a
     * hand-written log, and "Perf 4 · Fatigue — · Feeling —" is a line that says less than no
     * line at all.
     */
    debrief: (log) =>
      log.performance !== null && log.fatigue !== null && log.feeling !== null
        ? t("history.debrief", {
            performance: log.performance,
            fatigue: log.fatigue,
            feeling: log.feeling,
          })
        : null,
    heart: (log) => t(log.isFavourite ? "history.heart.remove" : "history.heart.save"),
  };
}

/** The feature's own `t`, taken from the hook rather than re-declared — so a signature change
 * in i18next is a build error here rather than a cast that hides one. */
export type Translate = ReturnType<typeof useTranslation<"logs">>["t"];

/**
 * WHICH SCORES GET THE ELAPSED CLOCK BESIDE THEM — the ones whose own figure is not a time.
 *
 * ⚠️ IT WAS "ANYTHING BUT A TIME", AND THAT BROKE THE MOMENT A SCORE STOPPED BEING A NUMBER
 * (BUD-370). A completion-scored piece shows "Completed" with no figure precisely because its
 * clock is the prescription read back; the old test would then have hung that same clock
 * beside it as a second figure, putting back the number the readout exists to remove. A piece
 * stopped early already IS its elapsed clock, so it needs no second copy either.
 */
export const SECONDARY_CLOCK: ReadonlySet<DebriefResult["kind"]> = new Set([
  "rounds",
  "minute",
  "lowestRound",
]);

/**
 * THE RESULT AS THE BARE NUMBER IT IS — "8:42", "12", "5 + 12".
 *
 * 🔑 SHARED BY THE DEBRIEF AND THE RECAP (BUD-372), which is the whole reason it is here and
 * not inside either screen: the score an athlete is shown the moment they finish and the score
 * they read back a week later must be the same words, and two private copies of this switch
 * are how "5 + 12" on one screen became "5" on the other.
 *
 * ⚠️ THE WORD IS NOT IN IT. The label (`debrief.result.label.*`) is the noun and this is the
 * figure — "ROUNDS / 12 rounds" says it twice.
 *
 * A time is FORMATTED here rather than translated: `m:ss` is a notation and not a sentence,
 * and it is the same notation in every language this app ships.
 */
export function resultText(t: Translate, result: DebriefResult): string {
  switch (result.kind) {
    /* THREE FIGURES THAT ARE ALL CLOCKS. `stoppedAt` is how far into a piece the athlete got
       before ending it — the LABEL above is what says it is a distance into the piece rather
       than a result, which is the whole of what separates it from `time`. */
    case "time":
    case "lowestRound":
    case "stoppedAt":
      return clockOf(result.value);
    /* THE ONE READOUT WITH NO FIGURE IN IT. An every-minute piece run to the end scores
       "completed" and nothing else — see `resultOf`, which refuses to hand back the clock. */
    case "completed":
      return t("debrief.result.completed");
    case "minute":
      return t("debrief.result.count", { n: result.value });
    /* BOTH HALVES WHERE THERE IS A SECOND ONE — "5 + 12", which is how a round-scored piece is
       written everywhere. A bare "5" for somebody who got most of the way through a sixth is
       the score for a worse session than they had. */
    case "rounds":
      return result.plus != null && result.plus > 0
        ? t("debrief.result.roundsPlus", { n: result.value, plus: result.plus })
        : t("debrief.result.count", { n: result.value });
  }
}

/**
 * THE CLOCK, MOVED TO `src/app/lib/duration.ts` AND RE-EXPORTED HERE.
 *
 * It was this feature's own helper, which was right while a duration was something only a
 * finished session had. The session clock ticks on five screens now, the lift log draws the
 * rest between two sets, and a heart-rate chart labels its axis — four features formatting one
 * quantity, and the sessions feature cannot import this one without closing a cycle.
 *
 * Re-exported rather than moved outright so every existing caller in this feature keeps its
 * import. There is exactly one implementation.
 */
export { clockOf } from "@/app/lib/duration";
